// Marquee Products - Auto-scrolling product carousel with swipe support

class MarqueeProducts {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.marquee-products__track');
    if (!this.track) return;

    this.speed = parseFloat(container.dataset.speed) || 30; // pixels per second
    this.pauseOnHover = container.dataset.pauseOnHover === 'true';
    const directionSetting = container.dataset.direction || 'left';
    this.direction = directionSetting === 'right' ? 1 : -1; // 1 for right (left to right movement), -1 for left (right to left movement)
    this.isPaused = false;
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.animationId = null;
    
    // Set initial position based on direction
    // For right-to-left: start at rightmost (negative position)
    // For left-to-right: start at 0
    this.currentPosition = directionSetting === 'right' ? this.getInitialRightPosition() : 0;

    this.init();
  }

  getInitialRightPosition() {
    // Calculate position to start from the right side
    // For right-to-left: show the last card on the right, new cards appear from left
    const items = Array.from(this.track.children);
    if (items.length === 0) return 0;
    
    const itemsCount = items.length / 2; // Half are duplicates
    const firstItem = items[0];
    const itemWidth = firstItem.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 32;
    const oneSetWidth = (itemWidth + gap) * itemsCount;
    const containerWidth = this.container.offsetWidth;
    
    // Start at negative position to show the rightmost card first
    // Position = -(oneSetWidth - containerWidth) shows the last card on the right
    return -(oneSetWidth - containerWidth);
  }

  init() {
    // Items are already duplicated in Liquid template for seamless loop
    // No need to duplicate again in JavaScript
    
    // Set initial position after a brief delay to ensure DOM is ready
    if (this.direction === 1) {
      // Right to left: wait for layout, then set initial position
      setTimeout(() => {
        this.currentPosition = this.getInitialRightPosition();
        this.track.style.transform = `translateX(${this.currentPosition}px)`;
      }, 100);
    }
    
    // Start animation
    this.startAnimation();

    // Add event listeners
    if (this.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.pause());
      this.container.addEventListener('mouseleave', () => this.resume());
    }

    // Touch/swipe support
    this.addSwipeSupport();

    // Pause on focus (accessibility)
    this.container.addEventListener('focusin', () => this.pause());
    this.container.addEventListener('focusout', () => this.resume());
  }

  startAnimation() {
    const animate = () => {
      if (!this.isPaused && !this.isDragging) {
        this.currentPosition += (this.speed * this.direction) / 60; // 60fps
        
        // Calculate the width of one set of items for seamless loop
        const items = Array.from(this.track.children);
        if (items.length > 0) {
          const itemsCount = items.length / 2; // Half are duplicates from Liquid
          const firstItem = items[0];
          const itemWidth = firstItem.offsetWidth;
          const gap = parseFloat(getComputedStyle(this.track).gap) || 32;
          const oneSetWidth = (itemWidth + gap) * itemsCount;
          const containerWidth = this.container.offsetWidth || window.innerWidth;
          
          if (this.direction === -1) {
            // Left to right: cards move from left to right (negative position increases)
            // Reset when scrolled past the first set
            if (this.currentPosition <= -oneSetWidth) {
              this.currentPosition = 0;
            }
          } else {
            // Right to left: cards appear from left, move to right (positive position increases)
            // Start showing rightmost card, new cards appear from left
            // Reset when we've scrolled one full set width
            const minPosition = -(oneSetWidth - containerWidth);
            const scrollDistance = this.currentPosition - minPosition;
            
            // When we've scrolled one full set width, reset back to start
            if (scrollDistance >= oneSetWidth) {
              // Reset to show the rightmost position again
              this.currentPosition = minPosition;
            }
          }
        }
        
        this.track.style.transform = `translateX(${this.currentPosition}px)`;
      }
      
      this.animationId = requestAnimationFrame(animate);
    };
    
    animate();
  }

  pause() {
    this.isPaused = true;
    this.container.classList.add('paused');
  }

  resume() {
    this.isPaused = false;
    this.container.classList.remove('paused');
  }

  normalizePosition() {
    // Normalize position to stay within loop bounds after dragging
    const items = Array.from(this.track.children);
    if (items.length > 0) {
      const itemsCount = items.length / 2;
      const firstItem = items[0];
      const itemWidth = firstItem.offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 32;
      const oneSetWidth = (itemWidth + gap) * itemsCount;
      const containerWidth = this.container.offsetWidth || window.innerWidth;
      
      if (this.direction === -1) {
        // Left to right: wrap around if out of bounds
        while (this.currentPosition <= -oneSetWidth) {
          this.currentPosition += oneSetWidth;
        }
        while (this.currentPosition > 0) {
          this.currentPosition -= oneSetWidth;
        }
      } else {
        // Right to left: wrap around if out of bounds
        const minPosition = -(oneSetWidth - containerWidth);
        const maxPosition = 0;
        
        while (this.currentPosition >= maxPosition) {
          this.currentPosition -= oneSetWidth;
        }
        while (this.currentPosition < minPosition) {
          this.currentPosition += oneSetWidth;
        }
      }
      
      this.track.style.transform = `translateX(${this.currentPosition}px)`;
    }
  }

  addSwipeSupport() {
    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalSwipe = false;
    let isVerticalSwipe = false;
    let touchMoved = false;

    // Touch events
    this.track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      this.startX = this.currentPosition;
      isHorizontalSwipe = false;
      isVerticalSwipe = false;
      touchMoved = false;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!touchMoved) {
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const deltaX = Math.abs(touchX - touchStartX);
        const deltaY = Math.abs(touchY - touchStartY);
        
        // Determine swipe direction after initial movement
        if (deltaX > 10 || deltaY > 10) {
          touchMoved = true;
          isHorizontalSwipe = deltaX > deltaY && deltaX > 10;
          isVerticalSwipe = deltaY > deltaX && deltaY > 10;
          
          // Only capture horizontal swipes for marquee
          if (isHorizontalSwipe) {
            this.isDragging = true;
            this.track.classList.add('dragging');
            this.pause();
            e.preventDefault();
          } else if (isVerticalSwipe) {
            // Allow vertical scrolling - don't prevent default
            this.isDragging = false;
            return;
          }
        }
      }
      
      // Only handle horizontal movement
      if (this.isDragging && isHorizontalSwipe) {
        const touchX = e.touches[0].clientX;
        const deltaX = touchX - touchStartX;
        
        e.preventDefault();
        const newPosition = this.startX + deltaX;
        this.currentPosition = newPosition;
        this.track.style.transform = `translateX(${this.currentPosition}px)`;
      }
    }, { passive: false });

    this.track.addEventListener('touchend', () => {
      if (this.isDragging) {
        this.normalizePosition();
        this.isDragging = false;
        this.track.classList.remove('dragging');
        this.resume();
      }
      isHorizontalSwipe = false;
      isVerticalSwipe = false;
      touchMoved = false;
    });

    // Mouse drag support
    this.track.addEventListener('mousedown', (e) => {
      this.startX = e.clientX - this.currentPosition;
      this.isDragging = true;
      this.track.classList.add('dragging');
      this.pause();
      e.preventDefault();
    });

    this.track.addEventListener('mousemove', (e) => {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.clientX - this.startX;
      this.currentPosition = x;
      this.track.style.transform = `translateX(${this.currentPosition}px)`;
    });

    this.track.addEventListener('mouseup', () => {
      if (this.isDragging) {
        this.normalizePosition();
        this.isDragging = false;
        this.track.classList.remove('dragging');
        this.resume();
      }
    });

    this.track.addEventListener('mouseleave', () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.track.classList.remove('dragging');
        this.resume();
      }
    });
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}

// Initialize all marquee products on page load
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.marquee-products__container');
  containers.forEach(container => {
    new MarqueeProducts(container);
  });
});

// Re-initialize when sections are loaded dynamically (theme editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.marquee-products__container');
    if (container) {
      new MarqueeProducts(container);
    }
  });
}
